import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketAPI, getAttachmentUrl } from '../services/api';
import { FiArrowLeft, FiCalendar, FiUser, FiAlertCircle, FiCheck, FiX } from 'react-icons/fi';
import './TicketDetail.css';

const TicketDetail = () => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await ticketAPI.getTicket(id);
      setTicket(response.data);
    } catch (error) {
      setError('Failed to load ticket details');
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Are you sure you want to withdraw this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      setUpdating(true);
      setError('');
      await ticketAPI.withdrawTicket(id, { user_id: user.id });
      setSuccess('Ticket withdrawn successfully');
      fetchTicket();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to withdraw ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      setError('Please select a status');
      return;
    }

    try {
      setUpdating(true);
      setError('');
      await ticketAPI.updateStatus(id, {
        status: newStatus,
        changed_by: user.id,
        notes: statusNotes,
      });
      setSuccess('Status updated successfully');
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
      fetchTicket();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status, isWithdrawn) => {
    if (isWithdrawn) return 'badge-withdrawn';
    switch (status) {
      case 'open':
        return 'badge-open';
      case 'in-progress':
        return 'badge-in-progress';
      case 'resolved':
        return 'badge-resolved';
      case 'closed':
        return 'badge-closed';
      default:
        return 'badge-open';
    }
  };

  if (loading) {
    return (
      <div className="ticket-detail-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-container">
        <div className="container">
          <div className="error-message">Ticket not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-detail-container">
      <div className="container">
        <div className="detail-header">
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <FiArrowLeft /> Back to Dashboard
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="detail-content">
          <div className="detail-main">
            <div className={`detail-card card fade-in ${ticket.is_withdrawn ? 'withdrawn' : ''}`}>
              <div className="ticket-status-header">
                <span className={`ticket-id-large ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.ticket_id}
                </span>
                <span className={`badge ${getStatusBadge(ticket.status, ticket.is_withdrawn)}`}>
                  {ticket.is_withdrawn ? 'Withdrawn' : ticket.status}
                </span>
              </div>

              <h1 className={`detail-title ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                {ticket.title}
              </h1>

              <div className="detail-meta-grid">
                <div className="meta-card">
                  <FiUser className="meta-icon" />
                  <div>
                    <div className="meta-label">Created By</div>
                    <div className="meta-value">{ticket.user_name}</div>
                  </div>
                </div>

                <div className="meta-card">
                  <FiCalendar className="meta-icon" />
                  <div>
                    <div className="meta-label">Created On</div>
                    <div className="meta-value">{formatDate(ticket.created_at)}</div>
                  </div>
                </div>

                <div className="meta-card">
                  <FiAlertCircle className="meta-icon" />
                  <div>
                    <div className="meta-label">Complaint Type</div>
                    <div className="meta-value">{ticket.complaint_type_name}</div>
                  </div>
                </div>

                {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                  <div className="meta-card">
                    <FiCalendar className="meta-icon" />
                    <div>
                      <div className="meta-label">Last Updated</div>
                      <div className="meta-value">{formatDate(ticket.updated_at)}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h2 className="section-title">Description</h2>
                <p className={`detail-description ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.description}
                </p>
              </div>

              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="detail-section">
                  <h2 className="section-title">Attachments</h2>
                  <div className="attachments-grid">
                    {ticket.attachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={getAttachmentUrl(attachment.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-card"
                      >
                        {attachment.file_type.startsWith('image/') ? (
                          <img
                            src={getAttachmentUrl(attachment.file_path)}
                            alt={attachment.file_name}
                            className="attachment-image"
                          />
                        ) : (
                          <div className="attachment-video-icon">🎥</div>
                        )}
                        <div className="attachment-info">
                          <span className="attachment-name">{attachment.file_name}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {!ticket.is_withdrawn && ticket.user_id === user.id && (
                <div className="detail-actions">
                  <button onClick={handleWithdraw} className="btn btn-secondary" disabled={updating}>
                    <FiX /> Withdraw Ticket
                  </button>
                  <button
                    onClick={() => setShowStatusModal(true)}
                    className="btn btn-primary"
                    disabled={updating}
                  >
                    <FiCheck /> Update Status
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="detail-sidebar">
            {ticket.status_history && ticket.status_history.length > 0 && (
              <div className="history-card card">
                <h2 className="section-title">Status History</h2>
                <div className="history-timeline">
                  {ticket.status_history.map((history) => (
                    <div key={history.id} className="history-item">
                      <div className="history-dot"></div>
                      <div className="history-content">
                        <div className="history-status">
                          {history.old_status && (
                            <span className="status-change">
                              {history.old_status} → {history.new_status}
                            </span>
                          )}
                          {!history.old_status && (
                            <span className="status-change">{history.new_status}</span>
                          )}
                        </div>
                        <div className="history-meta">
                          <span className="history-user">{history.changed_by}</span>
                          <span className="history-date">{formatDate(history.changed_at)}</span>
                        </div>
                        {history.notes && <p className="history-notes">{history.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Status</h2>
              <button onClick={() => setShowStatusModal(false)} className="modal-close">
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Status</label>
                <select
                  className="form-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="">Select status</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  className="form-textarea"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add notes about this status change..."
                  rows={4}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
                disabled={updating}
              >
                Cancel
              </button>
              <button onClick={handleStatusUpdate} className="btn btn-primary" disabled={updating}>
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;
